<script>
  /**
   * @fileoverview ChartCard component displays audience analytics data in a doughnut chart
   * with an accompanying data table. Supports various chart types (senders, recipients,
   * domains, browsers, devices, OS, countries, cities, gender, events) with appropriate
   * localized titles and column headers.
   *
   * @component ChartCard
   * @description Renders a doughnut chart and data table for audience insights data.
   * Shows top 5 entries with color-coded legend and handles empty data states gracefully.
   *
   * @prop {ChartData} chart - Chart data containing labels and datasets
   * @prop {string} typeKey - Chart type identifier for localization
   *
   * @example
   * <ChartCard
   *   chart={{
   *     labels: ['sender1@example.com', 'sender2@example.com'],
   *     datasets: [{
   *       label: 'Email Count',
   *       data: [45, 32],
   *       backgroundColor: ['#FF6384', '#36A2EB']
   *     }]
   *   }}
   *   typeKey="senders"
   * />
   */

  import { format } from 'svelte-i18n';

  import { Chart } from '@client/components/charts';

  import { staticHexColors } from '@common/utils/colors';

  /**
   * @typedef {Object} ChartDataset
   * @property {string} label - Dataset label for accessibility
   * @property {number[]} data - Numeric values for each chart segment
   * @property {string[]} backgroundColor - Colors for each chart segment
   * @property {number} borderWidth - Border width for chart segments
   * @property {number} hoverOffset - Pixel offset when hovering over segments
   *
   * @typedef {Object} ChartData
   * @property {string[]} labels - Human-readable labels for chart segments
   * @property {ChartDataset[]} datasets - Chart datasets (typically contains one dataset)
   */

  /**
   * Chart data containing labels and datasets for the doughnut chart.
   * Must include labels array and at least one dataset with data points.
   * @type {ChartData}
   */
  export let chart;

  /**
   * Chart type identifier used for localization and table headers.
   * Supported values: 'senders', 'recipients', 'domains', 'browser', 'device',
   * 'os', 'countries', 'cities', 'gender', 'events'.
   * @type {string}
   */
  export let typeKey;

  /**
   * Configuration options for the chart.
   * @type {import('chart.js').ChartOptions}
   */
  let options = {
    plugins: {
      title: {
        display: false
      },
      legend: {
        display: false
      }
    }
  };
</script>

<div class="audience-card">
  <div class="title">
    {$format('label.TOP_FIVE')}
    {#if typeKey === 'senders'}
      <b>{$format('label.SENDERS')}</b>
    {:else if typeKey === 'recipients'}
      <b>{$format('label.RECIPIENTS')}</b>
    {:else if typeKey === 'domains'}
      <b>{$format('label.DOMAINS')}</b>
    {:else if typeKey === 'browser'}
      <b>{$format('label.BROWSERS')}</b>
    {:else if typeKey === 'device'}
      <b>{$format('label.DEVICES')}</b>
    {:else if typeKey === 'os'}
      <b>{$format('label.OS')}</b>
    {:else if typeKey === 'countries'}
      <b>{$format('label.COUNTRIES')}</b>
    {:else if typeKey === 'cities'}
      <b>{$format('label.CITIES')}</b>
    {:else if typeKey === 'gender'}
      <b>{$format('label.GENDER')}</b>
    {:else if typeKey === 'events'}
      <b>{$format('label.EVENTS')}</b>
    {/if}
  </div>

  <Chart type="doughnut" bind:data={chart} bind:options height="160px" width="100%" />

  <div>
    <table class="table">
      <tr>
        <th>{$format('label.NAME')}</th>
        {#if typeKey === 'senders' || typeKey === 'recipients'}
          <th>{$format('label.EVENTS')}</th>
        {:else}
          <th>{$format('label.TOTAL')}</th>
        {/if}
      </tr>
      {#if !chart.labels.length}
        <tr>
          <td colspan="2">{$format('label.NO_DATA_AVAILABLE')}</td>
        </tr>
      {/if}

      {#each Object.entries(chart.labels) as [index, label]}
        <tr>
          <td class="chart-value-label">
            <span class="chart-value-dot" style="background-color: {staticHexColors[index]};">&nbsp;</span>
            {label}
          </td>
          <td>{chart.datasets[0].data[index]}</td>
        </tr>
      {/each}
    </table>
  </div>
</div>

<style lang="scss" src="./chart-card.scss"></style>
